import {
    type TradeInAemSearchType,
    tradeInAemContactCheckboxIds,
    TradeInAemContactFormId,
    tradeInAemContactTextItemIds,
    TradeInAemModelSelectType,
    TradeInAemNavStep,
    TradeInAemVersion,
} from "../utils/constants";
import type { TradeInAemStateType, TradeInAemStoreType } from "../redux/store";
import {
    TradeInAemFindVehicleStatus,
    type DateRangeMap,
    type TradeInAemDetailFormItem,
    type TradeInAemEHubPayload,
    type TradeInAemExtraVehicleDetailsPdfType,
    type TradeInAemInfoBlockContent,
    type TradeInAemVehicleNameType,
    type TradeInAemVehicleType,
    type TradeInAemVehicleValuationType,
    type TradeInAemVehicleValueType,
} from "../utils/types";
import { tradeInAemSetFormItemValue } from "../redux/actions/TradeInAemVehicleDetailsActions";
import { tradeInAemUpdateVehicleProp, tradeInAemVehicleSetVehicle } from "../redux/actions/TradeInAemVehicleActions";
import { tradeInAemSetStep } from "../redux/actions/TradeInAemNavActions";
import {
    tradeInAemResetQuote,
    tradeInAemSetQuote,
    tradeInAemSetQuoteFailed,
} from "../redux/actions/TradeInAemVehicleValueActions";
import Debug from "../../common-deprecated/Debug";
import { sendFormInfo } from "../../common-deprecated/utils/contactFormUtils";
import { setEHubFailed, setEHubLatestResponse, setEHubSubmitting } from "../redux/actions/TradeInAemUtilsActions";
import type { ErrorLogType } from "../../common-deprecated/types/CommonTypes";
import type { TradeInAemLabelType } from "../utils/labels";
import {
    tradeInAemResetResolveItems,
    tradeInAemSelectResolveItem,
    tradeInAemSetResolveFailed,
    tradeInAemSetResolveLoading,
    tradeInAemSetResolveVehicle,
} from "../redux/actions/TradeInAemFindVehicleActions";
import { getLastName, getLeadCategory, getTradeInAemAPI } from "../utils/utils";
import type { TradeInAemExternalDataType } from "../../common-deprecated/features/tradein/redux/reducers/TradeInAemExternalDataReducer";
import {
    setTradeInAemCheckboxValue,
    setTradeInAemContactTextValue,
} from "../redux/actions/TradeInAemContactFormActions";
import { prefillContactTextItemsData } from "../utils/testData";
import type { DealerFinderSettingsType } from "../../common-deprecated/components/AemDealerFinder";

// Base class for any TradeInCsl implementation.
// This could probably be implemented with objects as well, but having features like inheritance and protected properties seemed useful.
export abstract class TradeInCsl {
    // Having the store as a protected property prevents misuse of the store as it should only be accessible and used inside its own class.
    // Store is optional as there are use cases where we want to use CSL but don't have easy access to the entire store.
    private store: TradeInAemStoreType | null = null;

    setStore(store: TradeInAemStoreType): void {
        this.store = store;
    }

    /**
     * This method is used whenever the store is required in CSL methods.
     * It throws on purpose as you need to have called setStore before accessing.
     */
    protected getStore(): TradeInAemStoreType {
        if (!this.store) throw new Error("CSL store not initialized");
        return this.store;
    }

    // ----------------------------------------------------------------------
    // Static properties
    // ----------------------------------------------------------------------
    abstract enabledNavSteps: TradeInAemNavStep[];

    abstract enableVin: boolean;

    abstract enableLicensePlate: boolean;

    abstract enableResolve: boolean;

    abstract enablePreviouslySearchedVehicles: boolean;

    // Mapping used in the resolve dropdown. Each key used for a resolve dropdown should map to a label which is used as the title for the dropdown.
    // If a properly matching label doesnt exist yet it needs to be added to the country specific components.
    abstract resolveKeyMap: Record<string, TradeInAemLabelType>;

    abstract dealerFinderSettings: DealerFinderSettingsType;

    // The testPlates and testVins will be added in the debug modal
    testPlates: { value: string; description: string }[] = [];

    testVins: { value: string; description: string }[] = [];

    // When true the valuation value will be hidden from the user on the summary step.
    // This will not necessarily prevent the valuation from happening as we usually do want that to happen.
    // The valuation will still be shown inside the PDF. (Notice that this is required for THEL)
    // Add a separate setting in case it also needs to be hidden on the PDF
    hideValuationValueOnSummary: boolean = false;

    // ----------------------------------------------------------------------
    // Selectors
    // ----------------------------------------------------------------------

    /**
     * Helper selector to easily get the name of the current vehicle.
     */
    vehicleNameSelector = (state: TradeInAemStateType): TradeInAemVehicleNameType => {
        const { vehicle } = state.tradeInAemVehicle;
        if (!vehicle) return { name: "" };

        // We want to reference the active CSL implementation of getVehicleName which is why vehicleNameSelector is an arrow function.
        // If vehicleNameSelector was not an arrow function it would reference the getVehicleName function of the TradeInCsl abstract class and not the implementing class.
        return this.getVehicleName(vehicle);
    };

    abstract vehicleInfoBlockSelector(state: TradeInAemStateType): TradeInAemInfoBlockContent;

    /**
     * Retrieve the (optional) localised expire date of the current vehicle quote.
     */
    abstract vehicleQuoteExpirySelector(state: TradeInAemStateType): string;

    /**
     * Get the vehicle value to be shown on the summary/valuation step from state.
     */
    abstract vehicleValueSelector(state: TradeInAemStateType): TradeInAemVehicleValueType | null;

    /**
     * Get the plate or vin according to the current searchType.
     */
    abstract vehiclePlateOrVinSelector(state: TradeInAemStateType): string | null;

    // ----------------------------------------------------------------------
    // Non-selector functions
    // ----------------------------------------------------------------------

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    getExtDataFromValuation(valuation: TradeInAemVehicleValuationType): TradeInAemExternalDataType | null {
        Debug.error("No CSL implementation for fetchTradeInExtDataByQuoteId");
        return null;
    }

    /**
     * This method is called every time the trade-in component transitions into another step.
     */
    onStepTransition = async (target: TradeInAemNavStep): Promise<void> => {
        const { getState, dispatch } = this.getStore();
        const { tradeInAemVehicleValue, tradeInAemUtils } = getState();
        dispatch(tradeInAemSetStep(target));

        const valuationSteps = [TradeInAemNavStep.VehicleValue, TradeInAemNavStep.Summary];
        const targetIsValuationStep = valuationSteps.includes(target);
        const valuationIsInitialized = tradeInAemVehicleValue.valuation || tradeInAemVehicleValue.failed;

        // Prefill Trade-in with test data when query parameter "useTestData" is true
        // Prefilling with test data should happen after the vehicle was initialised so this happens at the "vehicleDetails" step
        if (target === TradeInAemNavStep.VehicleDetails && tradeInAemUtils.useTestData) {
            this.prefillFormsWithTestData();
        }

        // Fetch a vehicle quote when going to the value or summary step.
        if (targetIsValuationStep && !valuationIsInitialized) {
            await this.fetchVehicleQuote();
        }

        // When there is already an active valuation, and the user goes back, reset the valuation. Else this won't be fetched again in the future.
        if (!targetIsValuationStep && valuationIsInitialized) {
            dispatch(tradeInAemResetQuote());
        }

        // eHub submission happens when transitioning to summary.
        // The current implementation implies whenever the summary is rendered we will resend to eHub.
        // When the tradeInVersion is hidden we don't send a request to ehub
        if (tradeInAemUtils.version !== TradeInAemVersion.Hidden && target === TradeInAemNavStep.Summary) {
            await this.submitEHubPayload();
        }
    };

    /**
     * Used in vehicle details form to update a form value.
     * TODO This naming might become confusing vs contact form
     */
    setFormItemValue(formItemKey: string, formItemValue: string | string[]): void {
        const { dispatch, getState } = this.getStore();
        const { tradeInAemVehicleValue } = getState();

        // Propagate to state
        dispatch(tradeInAemSetFormItemValue(formItemKey, formItemValue));

        // Attach the new property to the vehicle object.
        dispatch(tradeInAemUpdateVehicleProp(formItemKey, formItemValue));

        // Clear the quote if it is available as it becomes invalid whenever a vehicle detail formItem has changed.
        if (tradeInAemVehicleValue.valuation) dispatch(tradeInAemSetQuote(null));

        // Also clear any valuation error states as changing form item values can change the eventual output.
        if (tradeInAemVehicleValue.failed) dispatch(tradeInAemSetQuoteFailed(false));
    }

    /**
     * Fetch a vehicle from the trade-in API and set it in the store.
     */
    abstract fetchVehicleResults(value: string, type: TradeInAemSearchType): Promise<void>;

    /**
     * Fetch the quote of a vehicle which usually goes through a trade-in API call (but not always, see TSW)
     */
    abstract fetchVehicleQuote(): Promise<void>;

    /**
     * Get the name/description for a vehicle.
     */
    abstract getVehicleName(vehicle: TradeInAemVehicleType): TradeInAemVehicleNameType;

    /**
     * Get a unique key for the given vehicle
     */
    abstract getVehicleKey(vehicle: TradeInAemVehicleType): string;

    /**
     * Get a valid month & year record. Currently only used for THEL.
     */
    getFormItemDropdownRange(): DateRangeMap {
        return {};
    }

    /**
     * Get the form structure to be used for the vehicle detail step.
     * Not a selector as this will be propagated to Redux.
     * This implementation can possibly be edited in the future depending on how we'll be reading the actual localisation.
     * @param prefill - Use the vehicle data to prefill the values of the vehicle details forms items.
     */
    getVehicleDetailFormItems(prefill: boolean): { [formItemKey: string]: TradeInAemDetailFormItem } {
        try {
            const { vehicleDetailsFormConfig } = this.getStore().getState().tradeInAemSettings;
            const { vehicle } = this.getStore().getState().tradeInAemVehicle;
            const formItems = vehicleDetailsFormConfig ? JSON.parse(vehicleDetailsFormConfig) : {};

            // Prefill the vehicle details forms items with data from the vehicle from the trade-in api
            // During the trade-in flow the values of the form are added to the vehicle object and those values are sent to the value api when requesting a valuation
            // When rendering a PDF we recollect those values and add them back in the vehicle details forms, so we can easier create the PDF
            if (prefill) {
                Object.keys(formItems).forEach((key) => {
                    if (vehicle?.[key]) {
                        formItems[key].value = vehicle[key];
                    }
                });
            }

            return formItems;
        } catch (ex) {
            Debug.error("Parsing the vehicleDetailsFormConfig failed");
        }
        return {};
    }

    /**
     * Retrieve vehicle data to be sent to eHub. Used in the getEHubPayload method.
     *
     * It is very possible not all fields are available in the relevant payload, in that case set the properties as an empty string.
     */
    abstract getEHubVehicleData(): TradeInAemEHubPayload["vehicle"];

    /**
     * Returns a list of all the information of a vehicle available in the Trade in API
     * All these details will be shown on the PDF
     */
    abstract getExtraVehicleDetailsPdf(): TradeInAemExtraVehicleDetailsPdfType;

    /**
     * Retrieve the payload to be sent to the DXP Forms gateway.
     */
    getEHubPayload = (): TradeInAemEHubPayload => {
        const currentState = this.getStore().getState();
        const { tradeInAemContact, tradeInAemDealer, tradeInAemModelSelect, commonSettings } = currentState;
        const { country, language, brand } = commonSettings;

        const selectedModel = tradeInAemModelSelect.modelList.find(
            (listItem) => listItem.id === tradeInAemModelSelect.selectedModelId,
        );
        const vehicleValue = this.vehicleValueSelector(currentState);

        return {
            // Default properties.
            country,
            language,
            brand,
            formType: "tradein",
            typeId: "TradeInOfferByEmail",
            // subtype: "standard", // TODO: implement otpValidation subtype in OR-11867
            contactInfo: {
                firstname: tradeInAemContact[TradeInAemContactFormId.FirstName]?.value || null,
                lastname: getLastName(
                    tradeInAemContact[TradeInAemContactFormId.FirstLastName]?.value,
                    tradeInAemContact[TradeInAemContactFormId.SecondLastName]?.value,
                ),
                street: tradeInAemContact[TradeInAemContactFormId.Address]?.value || null,
                housenr: tradeInAemContact[TradeInAemContactFormId.HouseNumber]?.value || null,
                postcode: tradeInAemContact[TradeInAemContactFormId.PostalCode]?.value || null,
                city: tradeInAemContact[TradeInAemContactFormId.City]?.value || null,
                email: tradeInAemContact[TradeInAemContactFormId.Email]?.value || null,
                phone: tradeInAemContact[TradeInAemContactFormId.Phone]?.value || null,
                dealerId: tradeInAemDealer.dealer?.id || null,
                gdpr:
                    tradeInAemContact[TradeInAemContactFormId.PrivacyDisclaimers]?.items.map((item) => ({
                        link: item.url || "",
                        consent: item.selected ? "true" : "false",
                    })) || [],
            },
            vehicle: this.getEHubVehicleData(),
            vehicleValue: {
                min: vehicleValue?.value || null,
                max: vehicleValue?.value || null,
                validityDate: vehicleValue?.validityDate || null,
            },
            interestedModel: {
                // Only fallback to none when the step is enabled/visible but nothing was selected (This means the skip button was chosen)
                type:
                    tradeInAemModelSelect.selectedCarType ||
                    (tradeInAemModelSelect.enabled ? TradeInAemModelSelectType.None : null),
                model: selectedModel?.name || null,
                modelCode: selectedModel?.code || null,
            },
            leadCategory: getLeadCategory(tradeInAemModelSelect.selectedCarType),
        };
    };

    /**
     * Push the eHub payload to the DXP Forms gateway.
     *
     * This isnt necessarily 'CSL' as chances are relatively low this logic will change across countries.
     * However, as all other fetch logic is contained in CSL it is added here for now.
     */
    async submitEHubPayload(): Promise<{ success: boolean }> {
        const { dispatch, getState } = this.getStore();
        const { commonSettings } = getState();

        const payload = this.getEHubPayload();
        dispatch(setEHubSubmitting(true, payload));
        dispatch(setEHubFailed(false));

        let success = false;

        try {
            const response = await sendFormInfo(payload, commonSettings);
            success = response.success;
            dispatch(setEHubLatestResponse(response));
        } catch (e) {
            Debug.error("Failed to send payload to eHub", e);
        }

        dispatch(setEHubFailed(!success));
        dispatch(setEHubSubmitting(false, payload));

        return { success };
    }

    /**
     * Validates the aem component settings and returns a list of errors which can be added to the Error Modal
     */
    getErrorsByValidatingComponentSettings(): ErrorLogType[] {
        const { getState } = this.getStore();
        const { tradeInAemSettings } = getState();
        type SettingErrorType = { title: string; message: string[]; innerMessage: string };
        const errors: SettingErrorType[] = [];

        const settingErrors: SettingErrorType = {
            title: "Settings",
            message: [],
            innerMessage:
                "Documentation can be found here: https://dxpwiki.toyota.eu/dxpdocumentation/componentsoverview/tradein",
        };

        // Setting: newModelList
        if (!tradeInAemSettings.newModelList.length && this.enabledNavSteps.includes(TradeInAemNavStep.ModelSelect)) {
            settingErrors.message.push("newModelList is a required setting in i18n when using the Model Select step");
        }
        // Setting: stockModelList
        if (!tradeInAemSettings.stockModelList.length && this.enabledNavSteps.includes(TradeInAemNavStep.ModelSelect)) {
            settingErrors.message.push("stockModelList is a required setting in i18n when using the Model Select step");
        }
        // Add settingErrors when there is at least one
        if (settingErrors.message.length) {
            errors.push(settingErrors);
        }

        return errors;
    }

    /**
     * Integrated the trade-in resolve API used to select a vehicle without a license plate or vin.
     *
     * See https://cde.toyota-europe.com/confluence/display/IT/Trade-in+API for more info.
     * The logic is set on a country level.
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    getResolveOptions(parsedResponse: Record<string, string>): boolean {
        if (this.enableResolve) Debug.error("No CSL implementation for fetchResolveOptions");
        return false;
    }

    /**
     * The formatting of the resolve response can differ per country. If not impelented by the country specific logic, the input gets returned as is.
     */
    formatResolveVehicleResponse(parsedResponse: Record<string, string>): Record<string, string> {
        return parsedResponse;
    }

    async fetchResolveVehicle(): Promise<void> {
        const { dispatch, getState } = this.getStore();
        const { tradeInAemFindVehicle, tradeInAemVehicle, commonSettings, tradeInAemSettings } = getState();

        // If the search (VIN / license plate) is insufficient, and there is no form yet, get the resolveOptions accordingly.
        if (
            tradeInAemFindVehicle.searchStatus === TradeInAemFindVehicleStatus.Insufficient &&
            !tradeInAemFindVehicle.resolveForm.length
        ) {
            this.getResolveOptions(tradeInAemFindVehicle.resolveVehicle);
            return;
        }

        // Clear the current vehicle as it becomes invalid whenever someone starts resolving.
        if (tradeInAemVehicle) dispatch(tradeInAemVehicleSetVehicle(null));

        // Retrieve the URL. Will obviously only work if the resolve functionality is supported for the current country.
        const url = `${getTradeInAemAPI(commonSettings, tradeInAemSettings)}/resolve`;
        // The initial implementation is based on TMI (which is closely related to TES).
        // Leaving this here for debugging purposes. (easy to test for example TZ)
        // const url = "https://tradein-prev.toyota-europe.com/it/it/resolve";

        dispatch(tradeInAemSetResolveLoading(true));

        const response = await fetch(url, {
            method: "POST",
            headers: this.getTradeInApiHeaders("POST"),
            body: JSON.stringify(tradeInAemFindVehicle.resolveVehicle),
        });

        if (response.status === 200) {
            // In short: Resolve API returns properties which varies depending on the country (see getResolveOptions() on country level, for example gr.ts).
            // If a property is found, render a dropdown, select one of the options, set this option in the new object and re-execute the request.
            const parsedResponse: Record<string, any> = await response.json(); // eslint-disable-line @typescript-eslint/no-explicit-any
            const formattedVehicle = this.formatResolveVehicleResponse(parsedResponse);
            dispatch(tradeInAemSetResolveVehicle(formattedVehicle));

            const newItem = this.getResolveOptions(formattedVehicle);

            dispatch(tradeInAemSetResolveLoading(false));
            if (tradeInAemFindVehicle.resolveFailed) dispatch(tradeInAemSetResolveFailed(false));

            if (!newItem) {
                // No new resolve select items found in the response which lets us assume we have a complete vehicle.
                // At this point parsedResponse becomes a trace-in vehicle, set it in store which should indicate the user can continue the flow.
                dispatch(tradeInAemVehicleSetVehicle(formattedVehicle));
            }
        } else {
            dispatch(tradeInAemSetResolveLoading(false));
            dispatch(tradeInAemSetResolveFailed(true));
        }
    }

    /**
     * Some resolve options from the api response should remain part of the resolveVehicle at all times, even when going back a few resolve steps.
     * Without defining them here, they could get removed when navigatig some steps back. Removing these could reset or break the resolve flow.
     */
    getStaticResolveOptions = (): string[] => [];

    /**
     * Triggered whenever the user selects an option in the resolve dropdown.
     */
    onResolveOptionSelect = (key: string, id: string): void => {
        const { dispatch, getState } = this.getStore();
        const { tradeInAemFindVehicle } = getState();
        const { resolveForm, resolveVehicle } = tradeInAemFindVehicle;

        // Update dropdown form item state.
        dispatch(tradeInAemSelectResolveItem(key, id));

        // Update the resolve vehicle object with the new value.
        const updatedVehicle = { ...resolveVehicle, [key]: id };

        // Clear any form items which appear under the resolve item.
        // This happens because all these items become irrelevant if the user selected another parent option.
        // We could possibly do this in the reducer as well but keeping it here to avoid putting too much logic in reducers.
        const foundItemIndex = resolveForm.findIndex((formItem) => formItem.vehicleProperty === key);

        // Also remove the removed dropdown item info from the vehicle object as this information is no longer valid as well.
        if (foundItemIndex < resolveForm.length - 1) {
            const itemsToKeep = [
                ...resolveForm.slice(0, foundItemIndex + 1).map((item) => item.vehicleProperty),
                ...this.getStaticResolveOptions(),
            ];

            dispatch(tradeInAemResetResolveItems(itemsToKeep));
            Object.keys(updatedVehicle).forEach((itemKey) => {
                if (!itemsToKeep.includes(itemKey)) {
                    delete updatedVehicle[itemKey];
                }
            });
        }

        dispatch(tradeInAemSetResolveVehicle(updatedVehicle));
        this.fetchResolveVehicle();
    };

    /**
     * This returns the headers which will be added to every request to the tradeInApi
     */
    getTradeInApiHeaders(method: "GET" | "POST"): HeadersInit | undefined {
        if (method === "POST") return { "Content-Type": "application/json" };

        return undefined;
    }

    /**
     * Will prefill the content (such as forms) with test data in the Trade-in flow
     * Prefilling with test data should happen after the vehicle was initialised so this happens at the "vehicleDetails" step
     * It's best to expand this on country level with CSL
     */
    prefillFormsWithTestData(): void {
        const { dispatch, getState } = this.getStore();
        const { tradeInAemContact } = getState();

        // Prefill contact form
        if (this.enabledNavSteps.includes(TradeInAemNavStep.ContactForm)) {
            // Prefill all available contact form text items with test data
            tradeInAemContactTextItemIds.forEach((id) => {
                if (tradeInAemContact[id]) {
                    dispatch(setTradeInAemContactTextValue(id, prefillContactTextItemsData[id]));
                }
            });

            // Select all contact for checkbox items
            tradeInAemContactCheckboxIds.forEach((id) => {
                if (tradeInAemContact[id]?.items) {
                    tradeInAemContact[id]?.items.forEach((item) => {
                        dispatch(setTradeInAemCheckboxValue(id, item.value, true));
                    });
                }
            });
        }
    }
}
